import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ColorPicker, Flex, InputNumber, Typography } from 'antd';

import FilterActions from '@components/propertyFilters/FilterActions';
import { useConfirm } from '@contexts/Confirm.context';
import { useProject } from '@contexts/Project.context';
import { useCollectionStore } from '@stores/useCollectionStore';

import SidebarTemplate from '../../../sidebars/sidebarElements/SidebarTemplate';
import {
	COLLECTION_SIDEBAR_IDS,
	HEXAGON_RESOLUTION_MAX,
	HEXAGON_RESOLUTION_MIN,
	HEXAGON_RESOLUTION_STEP,
	OBJECT_COLOR_PALETTE,
} from '../constants';
import { useHexagonConfigWrite } from '../hooks/useHexagonConfig';

const { Text } = Typography;

// Module-level so the preset swatches keep a stable identity across renders.
const COLOR_PRESETS = [{ label: 'Presets', colors: OBJECT_COLOR_PALETTE }];

// antd's picker returns lowercase hex where the store may hold uppercase, so
// re-picking the current colour must not read as a change.
const sameColor = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

// Module-level: the bounds are constants, so the label never needs recomputing.
const sizeRangeLabel = `${HEXAGON_RESOLUTION_MIN.toLocaleString()}–${HEXAGON_RESOLUTION_MAX.toLocaleString()}`;

/**
 * Hexagon Settings: a collection's cell size and base colour, in one panel
 * behind the gear button on the hexagon layer row. Mutually exclusive with the
 * classification panel (the layer owns that eviction), leaving the right side
 * to CollectionHexagonPanel.
 *
 * Both fields edit a draft and commit together through the shared
 * `FilterActions` footer as one PATCH. Applying is save-then-apply (see
 * useHexagonConfigWrite), so neither field can show a value that was never
 * stored, and a rejected save leaves the drafts dirty for a retry. Colour
 * still previews live via `hexagonColorDraft`, which the layer prefers while
 * this panel is open; cell size cannot preview — it needs a re-aggregation.
 *
 * @param {object} props
 * @param {string} props.collectionUuid
 * @param {boolean} props.isFetching - a hexagon re-aggregation is in flight
 */
const CollectionHexagonSettings = ({ collectionUuid, isFetching }) => {
	const { modifyLayersSidebarRef, setActiveSidebar, removeFromActiveSidebars } = useProject();
	const { confirm } = useConfirm();

	// The applied values: what the collection holds and the map is drawing.
	const resolution = useCollectionStore(s => s.hexagonResolution);
	const hexagonColor = useCollectionStore(s => s.hexagonColor);

	const { saveHexagonConfig, isSaving } = useHexagonConfigWrite(collectionUuid);

	// Size is drafted locally; colour in the store, so the layer can preview it.
	// `null` there means "unchanged" — `pickColor` normalizes a pick back to null
	// when it matches the saved colour, so a non-null draft means dirty.
	const [sizeDraft, setSizeDraft] = useState(resolution);
	const storedColorDraft = useCollectionStore(s => s.hexagonColorDraft);
	const setColorDraft = useCollectionStore(s => s.setHexagonColorDraft);
	const colorDraft = storedColorDraft ?? hexagonColor;

	// Re-seed on a save landing or a newly hydrated collection. A failed save
	// leaves the entry intact, because the store never moved.
	useEffect(() => setSizeDraft(resolution), [resolution]);

	// Teardown lives here, not in an ancestor: the layer hides its children on
	// an empty aggregation by returning null *while staying mounted*, so its own
	// cleanup would not fire for us. Left behind, a draft plus a still-registered
	// sidebar reopens this panel by itself, previewing a colour never applied.
	// A normal close goes through `handleHide`, where we stay mounted.
	useEffect(() => {
		return () => {
			useCollectionStore.getState().setHexagonColorDraft(null);
			removeFromActiveSidebars(COLLECTION_SIDEBAR_IDS.hexagonSettings);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// `sizeDraft` is a number or null (InputNumber clears to null), so validity is
	// decided on the coerced value once rather than at each use.
	const sizeValue = Number(sizeDraft);
	const isSizeValid =
		sizeDraft !== null &&
		Number.isFinite(sizeValue) &&
		sizeValue >= HEXAGON_RESOLUTION_MIN &&
		sizeValue <= HEXAGON_RESOLUTION_MAX;
	// An unusable draft counts as dirty, so Reset stays live to undo it and
	// closing asks first. Apply is blocked separately, through `canSave`.
	const isSizeDirty = !isSizeValid || sizeValue !== resolution;
	const isColorDirty = storedColorDraft !== null;
	const isDirty = isSizeDirty || isColorDirty;

	// The field deliberately carries no min/max: rc-input-number drops onChange
	// for an out-of-range keystroke and clamps silently on blur, which would keep
	// an unusable draft — and this warning — off screen entirely.
	const sizeStatus = !isSizeValid ? 'error' : isSizeDirty ? 'warning' : undefined;

	const isBusy = isFetching || isSaving;
	const canSave = isDirty && isSizeValid && !isBusy;

	const reset = useCallback(() => {
		setSizeDraft(resolution);
		// Dropping the draft also drops the map's preview — the layer falls back
		// to the saved colour the moment this is null.
		setColorDraft(null);
	}, [resolution, setColorDraft]);

	// Closing with unsaved drafts asks first — same copy and outcomes as the
	// collection and object filter panels.
	const handleHide = useCallback(async () => {
		if (!isDirty) return;

		const confirmed = await confirm({
			title: 'Discard changes?',
			message: 'You have unsaved changes. Are you sure you want to close without applying?',
			confirmLabel: 'Discard',
			cancelLabel: 'Keep editing',
		});

		if (confirmed) {
			reset();
		} else {
			// SidebarTemplate already removed the sidebar from activeSidebars
			// before this resolves — re-add it to keep the panel open.
			setActiveSidebar({
				sidebarId: COLLECTION_SIDEBAR_IDS.hexagonSettings,
				parentId: 'layerView',
			});
		}
	}, [isDirty, confirm, reset, setActiveSidebar]);

	// Picking the saved colour again is not a change, so it clears the draft
	// rather than storing an equal one. That keeps "draft is set" and "colour is
	// dirty" the same thing.
	const pickColor = hex => setColorDraft(sameColor(hex, hexagonColor) ? null : hex);

	// Both fields are sent every time. The endpoint replaces the whole `hexagon`
	// object and the write path merges over what is stored, so sending the pair
	// costs nothing and keeps this free of "which field changed" bookkeeping.
	const apply = () => {
		if (!canSave) return;
		saveHexagonConfig(
			{ size: sizeValue, color: colorDraft },
			// Cleared on success only: `hexagonColor` now holds this exact value so
			// the map does not change, and a failed save keeps both the preview and
			// the dirty state so Apply can simply be pressed again.
			{ onSuccess: () => setColorDraft(null) }
		);
	};

	if (!modifyLayersSidebarRef?.current) return null;

	return createPortal(
		<SidebarTemplate
			zindex="2"
			sidebarId={COLLECTION_SIDEBAR_IDS.hexagonSettings}
			header="Hexagon Settings"
			closeable
			onHide={handleHide}>
			<Flex vertical style={{ height: '100%', overflow: 'hidden' }}>
				<Flex vertical gap={16} style={{ flex: 1, padding: '12px 16px' }}>
					{/* Enter applies, so the field is usable without reaching for
					    the button. */}
					<Flex vertical gap={4}>
						<Text style={{ fontSize: '0.8rem' }}>Cell size (m²)</Text>
						<InputNumber
							aria-label="Hexagon cell size in square meters"
							step={HEXAGON_RESOLUTION_STEP}
							value={sizeDraft}
							onChange={setSizeDraft}
							onPressEnter={apply}
							disabled={isBusy}
							status={sizeStatus}
							style={{ width: '100%' }}
						/>
						<Text type="secondary" style={{ fontSize: '0.75rem' }}>
							Larger cells aggregate more detections into each hexagon. Allowed range{' '}
							{sizeRangeLabel} m².
						</Text>
					</Flex>

					{/* antd's own trigger, with `showText` so the hex is readable.
					    `disabledAlpha` keeps `toHexString()` at the 6 digits
					    hexToRgba accepts. `onChangeComplete` fires on release, so a
					    drag updates the draft once. `disabled` tracks only
					    `isSaving`: a save's onSuccess clears the draft and would
					    clobber a pick made during it. */}
					<Flex vertical gap={4}>
						<Text style={{ fontSize: '0.8rem' }}>Colour</Text>
						<ColorPicker
							value={colorDraft}
							presets={COLOR_PRESETS}
							disabledAlpha
							showText
							disabled={isSaving}
							onChangeComplete={color => pickColor(color.toHexString())}
							style={{ width: '100%', justifyContent: 'flex-start' }}
						/>
						<Text type="secondary" style={{ fontSize: '0.75rem' }}>
							Previews on the map as you pick. Used when no classification is applied.
						</Text>
					</Flex>

					{!isSizeValid && (
						<Text type="danger" style={{ fontSize: '0.75rem' }}>
							Cell size must be between {sizeRangeLabel} m².
						</Text>
					)}
				</Flex>

				{/* Shared footer — same Reset/Apply as the collection and object
				    filter panels. `isSaving` is handed the combined busy flag so the
				    buttons are also inert while a re-aggregation is in flight.
				    `canApply` blocks Apply on an unusable size while leaving Reset
				    live, so an emptied field can always be undone. */}
				<FilterActions
					isDirty={isDirty}
					canApply={canSave}
					isSaving={isBusy}
					onReset={reset}
					onApply={apply}
				/>
			</Flex>
		</SidebarTemplate>,
		modifyLayersSidebarRef.current
	);
};

export default CollectionHexagonSettings;
